import { h } from 'preact';
import { useMemo, useState, useEffect } from 'preact/hooks';
import { formatMoney } from 'utils/storefront/format-resource';
import Image from 'utils/image';
import PlusIcon from 'utils/icon/icon-plus.svg';
import MinusIcon from 'utils/icon/icon-minus.svg';
import clsx from 'clsx';
import { withCartContext } from './cart-context';
import checkInventoryAvailable from './inventory-checker';

const CartItem = (props) => {
  const { cart, line, index } = props;
  const [availableStock, setAvailableStock] = useState(Infinity);

  // Check inventory on component mount and when line quantity changes
  useEffect(() => {
    const checkInitialInventory = async () => {
      if (line.properties?._inventoryManagement !== 'shopify') {
        return;
      }

      const { maxQuantity: availableQuantity } = await checkInventoryAvailable(
        line.variant_id,
        line.quantity,
        cart,
        line.key,
      );

      setAvailableStock(availableQuantity);

      if (availableQuantity === 0) {
        const toastEvent = new CustomEvent('cart:notification', {
          detail: {
            message: `${line.product_title} is no longer available and will be removed from your cart.`,
            type: 'error',
            duration: 5000,
          },
        });
        document.dispatchEvent(toastEvent);
        cart.remove([line.key]);
        return;
      }
      if (availableQuantity < line.quantity) {
        const toastEvent = new CustomEvent('cart:notification', {
          detail: {
            message: `Only ${availableQuantity} ${line.product_title} are available. Quantity updated.`,
            type: 'warning',
            duration: 5000,
          },
        });
        document.dispatchEvent(toastEvent);

        cart.update([{ key: line.key, quantity: availableQuantity }]);
      }
    };

    checkInitialInventory();
  }, [line.variant_id, line.quantity]);

  const optionsNumber = line.options_with_values.filter((option) => option.value !== 'Default Title').length;

  const handleAdjustQuantity = async (quantity) => cart.update([{ key: line.key, quantity }]);
  const handleRemove = async () => {
    const payload = [line.key];

    await cart.remove(payload);

    // After removing, check if there's a gift wrap in cart
    const giftWrapItem = cart.state.items?.find((item) => item.properties._added_gift_wrap === 'true');

    if (giftWrapItem) {
      // Check remaining items, excluding both gift wrap and the item being removed
      const remainingItems = cart.state.items?.filter((item) => item.key !== giftWrapItem.key && item.key !== line.key);

      const hasEligibleItem = remainingItems?.some((item) => item.properties._gift_box_eligible === 'true');

      // If no eligible items remain, remove the gift wrap
      if (!hasEligibleItem) {
        await cart.remove([giftWrapItem.key]);
      }
    }
  };

  const isGift = useMemo(() => {
    if (line.properties._gwp) {
      return true;
    }
    return false;
  }, [props.line]);

  // line item link handling
  let lineItemHref = line.url;
  if (cart.mode === 'storefront') {
    lineItemHref = `/products/${line.merchandise.product.handle}`;
  }
  if (line.properties._isCustomProduct && line.properties._customProductRecipeID__c) {
    lineItemHref = `/pages/customizer?recipeID=${line.properties._customProductRecipeID__c}`;
  }

  // line item image handling
  let lineItemImage = line.children ? line.merchandise?.product?.image?.src : line.image;
  if (line.properties._isCustomProduct && line.properties._thumbnailUrl) {
    lineItemImage = line.properties._thumbnailUrl;
  }

  const lineItem = useMemo(
    () => (
      <div>
        <div
          key={line.id}
          className={clsx('px-15 py-28', index !== 0 && 'border-t border-grey-5')}
        >
          <div className="flex gap-15 font-franklin">
            <div className="flex w-[100px] items-center justify-center rounded border border-grey-5">
              {line.properties._isCustomProduct ? (
                <img
                  src={lineItemImage}
                  alt={`${line.product_title} image`}
                  className="block w-full object-contain"
                  width="150"
                  height="150"
                />
              ) : (
                <Image
                  src={lineItemImage}
                  classes="block object-contain w-full"
                  alt={`${line.product_title} image`}
                  width="150"
                  height="150"
                  srcWidths={{ sm: 200, md: 200 }}
                />
              )}
            </div>
            <div className="flex flex-1 flex-col gap-8">
              <a
                className={clsx(
                  'body-14-med text-faded-black',
                  line.product_type === 'Donation' && 'pointer-events-none',
                )}
                href={lineItemHref}
              >
                {cart.mode === 'storefront' ? line.merchandise?.product.title : line.product_title}
              </a>

              <div className="flex flex-wrap items-center gap-4">
                <div className="body-14 flex flex-wrap items-center gap-4">
                  {parseFloat(line.properties?._compare_at_price || 0) * 100 * line.quantity > line.line_price && (
                    <span className="line-through">
                      {formatMoney({
                        amount: parseFloat(line.properties?._compare_at_price || 0) * line.quantity,
                        currencyCode: window.TMW?.localization?.currencyCode,
                      })}
                    </span>
                  )}{' '}
                  {line.original_line_price > line.discounted_price * line.quantity &&
                    !line.properties?._compare_at_price && (
                      <span className="line-through">
                        {formatMoney({
                          amount: parseFloat(line.original_line_price / 100 || 0),
                          currencyCode: window.TMW?.localization?.currencyCode,
                        })}
                      </span>
                    )}
                  <span
                    className={
                      parseFloat(line.properties?._compare_at_price || 0) * 100 * line.quantity > line.line_price ||
                      line.original_line_price > line.discounted_price * line.quantity
                        ? 'text-error'
                        : ''
                    }
                  >
                    {line.original_line_price > line.discounted_price &&
                      formatMoney({
                        amount: (line.discounted_price * line.quantity) / 100,
                        currencyCode: window.TMW?.localization?.currencyCode,
                      })}
                    {line.original_line_price === line.discounted_price &&
                      formatMoney({
                        amount: line.line_price / 100,
                        currencyCode: window.TMW?.localization?.currencyCode,
                      })}
                  </span>
                </div>
                {isGift && (
                  <div className="body-12-med ml-2 rounded-full bg-surface-yellow px-10 py-5">
                    {window.TMW.Strings.cart.freeGift}
                  </div>
                )}
                {line.properties._promoBadgeBackgroundColor &&
                  line.properties._promoBadgeTextColor &&
                  line.properties._promoBadgeText && (
                    <div
                      className="body-12-med ml-2 rounded-full px-10 py-5"
                      style={{
                        backgroundColor: line.properties._promoBadgeBackgroundColor,
                        color: line.properties._promoBadgeTextColor,
                      }}
                    >
                      {line.properties._promoBadgeText}
                    </div>
                  )}
              </div>
              {line.discounts && line.discounts.length > 0 && (
                <div className="flex flex-col gap-2">
                  {line.discounts.map((discount) => (
                    <span
                      key={discount.title}
                      className="leading-14 body-12-med w-fit rounded-full bg-[#ffdedf] px-12 py-4 text-black"
                    >
                      {discount.title} (-
                      {formatMoney({
                        amount: discount.amount / 100,
                        currencyCode: window.TMW?.localization?.currencyCode,
                      })}
                      )
                    </span>
                  ))}
                </div>
              )}

              {optionsNumber > 0 && (
                <ul className="m-0 flex list-none flex-wrap gap-4 p-0">
                  {line.options_with_values.map(
                    (option, i) =>
                      option.value !== 'Default Title' &&
                      line.product_type !== 'Donation' &&
                      line.product_title !== 'eGift Card' && (
                        <li
                          className="body-12-light m-0 flex gap-4 pb-4"
                          key={option.value}
                        >
                          <span>{option.value}</span>
                          {i < optionsNumber - 1 && <span>|</span>}
                        </li>
                      ),
                  )}
                </ul>
              )}

              {line.sku !== '' && line.product_type !== 'Donation' && line.product_title !== 'eGift Card' && (
                <p className="body-12-light">
                  {window.TMW.Strings.cart.item} #{line.sku}
                </p>
              )}

              {line.properties?._delayedShippingMessage && (
                <div
                  className="[&>p]:body-12-light [&>ol]:body-12-light [&>ul]:body-12-light [&>*>a]:body-12-light [&>*>a]:underline [&>p>a]:underline-offset-2"
                  dangerouslySetInnerHTML={{ __html: line.properties._delayedShippingMessage }}
                ></div>
              )}

              {line.properties?._gift_box_eligible === 'false' && (
                <div className="body-12-light">This product makes your order ineligible for a gift box.</div>
              )}

              <div className={`flex items-end ${line.product_type === 'Donation' ? 'justify-end' : 'justify-between'}`}>
                {line.product_type !== 'Donation' && !isGift && line.sku !== 'GIFTWRAP00000' && (
                  <div className="rounded-40 px-14 flex h-40 w-[95px] items-center rounded border border-faded-black py-8 text-14 leading-100">
                    <button
                      onClick={() => handleAdjustQuantity(line.quantity - 1)}
                      aria-label={window.TMW.Strings.cart.remove}
                      className={clsx(
                        'disabled:text-gray-2 flex h-full w-32 items-center justify-center focus:ring-offset-0',
                        line.quantity <= 1 ? 'text-grey-2' : 'text-faded-black',
                      )}
                      disabled={line.quantity <= 1}
                    >
                      <MinusIcon />
                    </button>
                    <input
                      type="number"
                      value={line.quantity}
                      onBlur={(e) => handleAdjustQuantity(parseInt(e.target.value, 10))}
                      max={availableStock}
                      aria-label={window.TMW.Strings.product.quantityInput}
                      className="flex h-32 w-28 items-center justify-center border-none p-0 text-center font-weight-medium text-faded-black"
                    />
                    <button
                      onClick={() => handleAdjustQuantity(line.quantity + 1)}
                      aria-label={window.TMW.Strings.cart.add}
                      className={clsx(
                        'disabled:text-gray-2 flex h-full w-32 items-center justify-center  focus:ring-offset-0',
                        line.quantity >= availableStock ? 'text-grey-2' : 'text-faded-black',
                      )}
                      disabled={line.quantity >= availableStock}
                    >
                      <PlusIcon />
                    </button>
                  </div>
                )}

                <button
                  className="body-12-light underline decoration-grey-3 hover:text-smoky-blue"
                  onClick={() => handleRemove()}
                >
                  {window.TMW.Strings.cart.remove}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
    [line, isGift, availableStock],
  );

  return lineItem;
};

export default withCartContext(CartItem);
